// @flow
import cookieToken from '../../helpers/cookieToken';
import {route} from "../../helpers/common";
import {all, call, fork, put, take, takeEvery} from 'redux-saga/effects';

import { fetchJSON } from '../../helpers/api';

import {
    appLoad,
    appRedirect,
    appUserDisconnect,
    loginUserSuccess,
    loginUserFailed,
    registerUserSuccess,
    registerUserFailed,
    forgetPasswordSuccess,
    forgetPasswordFailed,
    resetPasswordSuccess,
    resetPasswordFailed,
    fallbackToCaptchaV2,
} from '../actions';
import { isPluginMode } from '../common';
import { setPluginOauth2Url, setPluginToken } from './actions';


/**
 * Login the user
 * @param {*} payload - username and password
 */
function* login({ payload: { email, password } }) {
    const options = {
        body: JSON.stringify({ email: email, password }),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    };

    const pluginMode = yield* isPluginMode();

    try {
        const response = yield call(fetchJSON, '/login/email', options, true);

        if (pluginMode) {
            // Set the token to the cookie instead
            cookieToken.setPluginToken(response.token);
            yield put(setPluginToken(response.token));
        } else {
            cookieToken.set(response.token);
        }

        yield put(loginUserSuccess());
        yield put(appLoad());
    } catch (error) {
        let message = 'Unexpected error';
        if (error.message) {
            message = error.message;
        }
        yield put(loginUserFailed(message));
    }
}

/**
 * Login the user via third party services
 */
const makeThirdPartyLogin = (method) => function* ({ payload: { url: urlPayload } }) {
    const baseUrl = 'http' + (import.meta.env.VITE_USE_HTTPS==='1'?'s':'') + '://' +  import.meta.env.VITE_DOMAIN_NAME;

    const pluginMode = yield* isPluginMode();

    try {
        const url = `${baseUrl}${import.meta.env.VITE_BASE_PATH}/login/${method}`;

        // If plugin mode, we need to redirect to the login page instead of opening a new window
        if (pluginMode && urlPayload) {
            // request to the URL
            const response = yield call(fetchJSON, `/login/${method}`, {
                body: JSON.stringify({ url: urlPayload }),
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });

            // Got the auth URL, redirect to it
            yield put(setPluginOauth2Url(response));
        } else {
            window.open(url, '_blank', 'width=450,height=600' );

            const response = yield call(() => {
                return new Promise((resolve)=>{
                    window.addEventListener('message', (message) => {
                        if (message.origin !== baseUrl) {
                            return;
                        }
                        resolve(message.data);
                    });
                });
            });

            if (response.error) {
                throw new Error(response.error);
            }

            if (pluginMode) {
                // Set the token to the cookie instead
                cookieToken.setPluginToken(response.token);
                yield put(setPluginToken(response.token));
            } else {
                cookieToken.set(response.token);
            }

            yield put(loginUserSuccess());
            yield put(appLoad());
        }
    } catch (error) {
        let message = 'Unexpected error';
        if (error.message) {
            message = error.message;
        }
        yield put(loginUserFailed(message));
    }
}

/**
 * Logout the user
 * @param {*} param0
 */
function* logout() {
    try {
        cookieToken.remove();
        cookieToken.removePluginToken();
        localStorage.removeItem('user');

        yield put(appUserDisconnect());
    } catch (error) {}
}

/**
 * Register the user
 */
function* register({ payload: { name, email, password, recaptchaToken, isV2 } }) {
    const options = {
        body: JSON.stringify({ name, email, password, recaptchaToken, v: isV2 ? 'v2' : 'v3' }),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    };

    const pluginMode = yield* isPluginMode();

    try {
        const response = yield call(fetchJSON, '/login/register', options);

        if (pluginMode) {
            // Set the token to the cookie instead
            cookieToken.setPluginToken(response.token);
            yield put(setPluginToken(response.token));
        } else {
            cookieToken.set(response.token);
        }

        yield put(registerUserSuccess(response));
        yield put(appLoad());
        yield take('APP_LOADED');
        yield put(appRedirect(route('domain.add')));
    } catch (error) {
        if (error.fallbackV2) {
            // Trigger V2 fallback
            yield put(fallbackToCaptchaV2());
            yield put(registerUserFailed('Automatic captcha validation failed, please do it manually'));
        } else if (error.message) {
            yield put(registerUserFailed(error.message));
        } else {
            yield put(registerUserFailed(error));
        }
    }
}

/**
 * forget password
 */
function* forgetPassword({ payload: {email, recaptchaToken, isV2} }) {
    const options = {
        body: JSON.stringify({ email: {email, recaptchaToken, v: isV2 ? 'v2' : 'v3'} }),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    };

    try {
        const response = yield call(fetchJSON, '/login/forget', options);
        yield put(forgetPasswordSuccess(response));
    } catch (error) {
        if (error.fallbackV2) {
            // Trigger V2 fallback
            yield put(fallbackToCaptchaV2());
            yield put(forgetPasswordFailed('Automatic captcha validation failed, please do it manually'));
        } else if (error.message) {
            yield put(forgetPasswordFailed(error.message));
        } else {
            yield put(forgetPasswordFailed('Unexpected error'));
        }
    }
}

/**
 * reset password
 */
function* resetPassword({ payload: { email, token, password } }) {
    const options = {
        body: JSON.stringify({email, token, password}),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    };

    try {
        const response = yield call(fetchJSON, '/login/reset', options);
        yield put(resetPasswordSuccess(response));
    } catch (error) {
        let message = 'Unexpected error';

        if (error.message) {
            message = error.message;
        }

        if (error.errors) {
            const [firstError] = Object.values(error.errors);

            if (firstError) {
                [message] = firstError;
            }
        }

        yield put(resetPasswordFailed(message));
    }
}

export function* watchLoginUser(): any {
    yield takeEvery('LOGIN_USER', login);
}

export function* watchLoginUserGoogle(): any {
    yield takeEvery('LOGIN_USER_GOOGLE', makeThirdPartyLogin('google'));
}

export function* watchLoginUserMicrosoft(): any {
    yield takeEvery('LOGIN_USER_MICROSOFT', makeThirdPartyLogin('microsoft'));
}

export function* watchLogoutUser(): any {
    yield takeEvery('LOGOUT_USER', logout);
}

export function* watchRegisterUser(): any {
    yield takeEvery('REGISTER_USER', register);
}

export function* watchForgetPassword(): any {
    yield takeEvery('FORGET_PASSWORD', forgetPassword);
}

export function* watchResetPassword(): any {
    yield takeEvery('RESET_PASSWORD', resetPassword);
}

function* authSaga(): any {
    yield all([fork(watchLoginUser), fork(watchLoginUserGoogle), fork(watchLoginUserMicrosoft), fork(watchLogoutUser), fork(watchRegisterUser), fork(watchForgetPassword), fork(watchResetPassword)]);
}

export default authSaga;
